import { useEffect, useState, type FormEvent } from 'react';
import { X, Loader2, Save } from 'lucide-react';
import { useApi, type HomeCamera, type CameraCodec } from '../lib/api';

interface Props {
  homeId: string;
  /** When provided, the modal is in "edit" mode and cameraId is locked. */
  camera?: HomeCamera | null;
  onClose: () => void;
  onSaved: (camera: HomeCamera) => void;
}

interface DraftState {
  cameraId: string;
  label: string;
  role: string;
  rtspUrl: string;
  rtspSubUrl: string;
  codec: CameraCodec;
  portrait: boolean;
  ip: string;
}

const EMPTY: DraftState = {
  cameraId: '',
  label: '',
  role: '',
  rtspUrl: '',
  rtspSubUrl: '',
  codec: 'h264',
  portrait: false,
  ip: '',
};

const CAMERA_ID_REGEX = /^[a-z0-9]+$/;

/**
 * Add or edit a HomeCamera. Used by CamerasList. The cameraId is the primary key
 * and is locked once a camera exists; everything else can be updated.
 */
export function CameraEditModal({ homeId, camera, onClose, onSaved }: Props) {
  const api = useApi();
  const isEdit = !!camera;
  const [draft, setDraft] = useState<DraftState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof DraftState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed the draft when opening for an existing camera.
  useEffect(() => {
    if (camera) {
      setDraft({
        cameraId: camera.cameraId,
        label: camera.label,
        role: camera.role,
        rtspUrl: camera.rtspUrl,
        rtspSubUrl: camera.rtspSubUrl ?? '',
        codec: camera.codec,
        portrait: camera.portrait ?? false,
        ip: camera.ip ?? '',
      });
    } else {
      setDraft(EMPTY);
    }
    setErrors({});
    setSubmitError(null);
  }, [camera]);

  // Close on Escape — small UX nicety expected of any modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const update = <K extends keyof DraftState>(k: K, v: DraftState[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof DraftState, string>> = {};
    if (!draft.cameraId.trim()) next.cameraId = 'Required';
    else if (!CAMERA_ID_REGEX.test(draft.cameraId))
      next.cameraId = 'Lowercase letters and digits only (e.g. cam5)';
    if (!draft.label.trim()) next.label = 'Required';
    if (!draft.role.trim()) next.role = 'Required';
    if (!draft.rtspUrl.trim()) next.rtspUrl = 'Required';
    else if (!draft.rtspUrl.startsWith('rtsp://'))
      next.rtspUrl = 'Must start with rtsp://';
    if (draft.rtspSubUrl && !draft.rtspSubUrl.startsWith('rtsp://'))
      next.rtspSubUrl = 'Must start with rtsp://';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const body: Partial<HomeCamera> = {
        cameraId: draft.cameraId.trim(),
        label: draft.label.trim(),
        role: draft.role.trim(),
        rtspUrl: draft.rtspUrl.trim(),
        codec: draft.codec,
        portrait: draft.portrait,
      };
      const sub = draft.rtspSubUrl.trim();
      if (sub) body.rtspSubUrl = sub;
      const ip = draft.ip.trim();
      if (ip) body.ip = ip;
      const { camera: saved } = await api.upsertHomeCamera(homeId, body);
      onSaved(saved);
    } catch (err: any) {
      setSubmitError(err.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm py-10 px-4"
      onClick={(e) => {
        // Click on backdrop closes; clicks on the inner card don't bubble here.
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="camera-edit-modal-title"
        className="w-full max-w-lg bg-white border border-slate-200 rounded-xl shadow-glow"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 id="camera-edit-modal-title" className="text-lg font-semibold text-brand-ink">
            {isEdit ? 'Edit camera' : 'Add camera'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-slate-600 hover:text-brand-ink p-1 rounded hover:bg-slate-100 disabled:opacity-40"
            aria-label={isEdit ? 'Close edit camera dialog' : 'Close add camera dialog'}
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          <Field label="Camera ID" hint="lowercase letters/digits, used as primary key" error={errors.cameraId}>
            <input
              required
              value={draft.cameraId}
              onChange={(e) => update('cameraId', e.target.value)}
              disabled={isEdit || saving}
              placeholder="cam5"
              className={fieldClass}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Label" error={errors.label}>
              <input
                required
                value={draft.label}
                onChange={(e) => update('label', e.target.value)}
                disabled={saving}
                placeholder="Living Room"
                className={fieldClass}
              />
            </Field>
            <Field label="Role" hint="e.g. interior, driveway" error={errors.role}>
              <input
                required
                value={draft.role}
                onChange={(e) => update('role', e.target.value)}
                disabled={saving}
                placeholder="interior"
                className={fieldClass}
              />
            </Field>
          </div>
          <Field
            label="RTSP URL"
            hint={camera?.rtspCredentialsSet
              ? 'password saved and hidden; keep this host to keep it, or type rtsp://user:newpass@… to change it'
              : 'must start with rtsp://'}
            error={errors.rtspUrl}
          >
            <input
              required
              value={draft.rtspUrl}
              onChange={(e) => update('rtspUrl', e.target.value)}
              disabled={saving}
              placeholder="rtsp://192.168.1.10:554/stream1"
              className={`${fieldClass} font-mono`}
            />
          </Field>
          <Field label="RTSP sub-stream URL" hint="optional low-res companion stream" error={errors.rtspSubUrl}>
            <input
              value={draft.rtspSubUrl}
              onChange={(e) => update('rtspSubUrl', e.target.value)}
              disabled={saving}
              placeholder="rtsp://192.168.1.10:554/stream2"
              className={`${fieldClass} font-mono`}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Codec">
              <select
                value={draft.codec}
                onChange={(e) => update('codec', e.target.value as CameraCodec)}
                disabled={saving}
                className={fieldClass}
              >
                <option value="h264">h264</option>
                <option value="h265">h265</option>
              </select>
            </Field>
            <Field label="IP address" hint="optional, for diagnostics">
              <input
                value={draft.ip}
                onChange={(e) => update('ip', e.target.value)}
                disabled={saving}
                placeholder="192.168.1.10"
                className={`${fieldClass} font-mono`}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 select-none">
            <input
              type="checkbox"
              checked={draft.portrait}
              onChange={(e) => update('portrait', e.target.checked)}
              disabled={saving}
              className="accent-brand-ink"
            />
            <span className="text-sm text-brand-ink">Portrait orientation</span>
          </label>

          {submitError && (
            <div className="p-2.5 rounded bg-red-50 border border-red-200 text-red-700 text-xs">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 -mx-5 px-5 -mb-4 pb-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 rounded text-sm text-ink-800 hover:text-brand-ink border border-slate-300 hover:border-slate-400 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm bg-brand-ink hover:bg-ink-800 text-brand-bone border border-brand-ink disabled:opacity-40"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add camera'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const fieldClass =
  'w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-sm text-brand-ink placeholder:text-slate-500 focus:border-brand-ink focus:outline-none disabled:opacity-50';

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-medium text-brand-ink">{label}</span>
        {hint && !error && <span className="text-[10px] text-slate-600">{hint}</span>}
      </div>
      {children}
      {error && <div className="mt-1 text-[11px] text-alert-breach">{error}</div>}
    </label>
  );
}
